import { useUpdateCustomPlanMeasures } from "mutations/CustomPlans/useUpdateCustomPlanMeasures";
import useCustomPlanSidebarState from "recoilStore/CustomPlan/useCustomPlanSidebarState";
import useQueryBuilding from "ApiLayer/Building/queryBuildingModel";
import {
    addProjectToMeasures,
    editProjectInMeasures,
    getSelectedMeasuresUids,
    getSelectedMeasureUid,
    getSelectedPlan,
    measuresToMeasureInput,
    removeProjectsFromMeasures,
} from "./useCustomPlanUpdate.utils";

interface UseCustomPlanUpdateParams {
    buildingModelUid: string;
    buildingModelCarbonReductionPlanId: string;
}

export const useCustomPlanUpdate = ({
    buildingModelUid,
    buildingModelCarbonReductionPlanId,
}: UseCustomPlanUpdateParams) => {
    const { data: buildingModel } = useQueryBuilding(buildingModelUid);
    const { mutate, isLoading } = useUpdateCustomPlanMeasures();
    const { resetSelections } = useCustomPlanSidebarState();

    const updateCustomPlanMeasures = (
        updateType: "edit" | "delete" | "create" | "remodel" | "reorder",
        options: {
            selectedRows?: CustomPlanDataSource[];
            measure?: CarbonReductionMeasureInput;
            reorderedMeasures?: CarbonReductionMeasure[];
            onSuccess?: () => void;
            onComplete?: () => void;
        } = {}
    ) => {
        if (buildingModel === undefined) return;
        const planToModify = getSelectedPlan(buildingModel, buildingModelCarbonReductionPlanId);

        const measures = planToModify?.plan?.carbonReductionMeasures;
        if (!measures) return;
        const measureInputs = measuresToMeasureInput(measures);
        let updatedMeasures: CarbonReductionMeasureInput[] = [];

        if (updateType === "remodel") {
            updatedMeasures = measureInputs;
        }

        if (updateType === "edit") {
            const { selectedRows = [], measure } = options;
            if (!measure) return;
            const selectedMeasureUid = getSelectedMeasureUid(selectedRows);
            updatedMeasures = editProjectInMeasures(measureInputs, selectedMeasureUid, measure);
        }

        if (updateType === "reorder") {
            const { reorderedMeasures } = options;
            if (!reorderedMeasures) return;
            updatedMeasures = measuresToMeasureInput(reorderedMeasures);
        }

        if (updateType === "delete") {
            const { selectedRows = [] } = options;
            const selectedCarbonReductionMeasureUids = getSelectedMeasuresUids(selectedRows);
            updatedMeasures = removeProjectsFromMeasures(measureInputs, selectedCarbonReductionMeasureUids);
        }

        if (updateType === "create") {
            const { measure } = options;
            if (!measure) return;
            updatedMeasures = addProjectToMeasures(measureInputs, measure);
        }
        mutate(
            {
                customPlanId: buildingModelCarbonReductionPlanId,
                buildingModelUid,
                measures: updatedMeasures || [],
            },
            {
                onSuccess: () => {
                    options.onSuccess?.();
                    resetSelections();
                },
            }
        );
    };

    return {
        updateCustomPlanMeasures,
        isLoading,
    };
};
